import { useEffect } from 'react';
import isFunction from 'lodash/isFunction';
// eslint-disable-next-line no-restricted-imports
import { useRouter } from 'next/router';

// https://github.com/vercel/next.js/issues/2476#issuecomment-843311387
const usePromptNavigation = (
  enabled: boolean | ((url?: string) => boolean),
  message = 'Are you sure that you want to leave?',
) => {
  const router = useRouter();

  useEffect(() => {
    let isWarned = false;

    const isEnabled = (url?: string) => (isFunction(enabled) ? enabled(url) : enabled);

    const onRouteChangeStart = (url: string) => {
      if (router.asPath !== url && !isWarned && isEnabled(url)) {
        isWarned = true;
        if (window.confirm(message)) {
          router.push(url);
        } else {
          isWarned = false;
          router.events.emit('routeChangeError');
          router.replace(router, router.asPath, { shallow: true });
          throw 'Abort route change. Please ignore this error.';
        }
      }
    };

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isWarned && isEnabled()) {
        event.returnValue = message;
        return message;
      }
      return null;
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    window.addEventListener('beforeunload', onBeforeUnload);
    router.beforePopState(({ url }) => {
      if (router.asPath !== url && !isWarned && isEnabled()) {
        isWarned = true;
        if (window.confirm(message)) {
          return true;
        } else {
          isWarned = false;
          router.events.emit('routeChangeError');
          window.history.pushState(null, '', url);
          router.replace(router, router.asPath, { shallow: true });
          return false;
        }
      }
      return true;
    });

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      window.removeEventListener('beforeunload', onBeforeUnload);
      router.beforePopState(() => true);
    };
  }, [router, message, enabled]);
};

export default usePromptNavigation;
